import React, { ReactNode } from 'react';
import { FormattedDate } from 'react-intl';
import { cx } from '@linaria/core';
import { styled } from '@linaria/react';
import { BottomSheet } from '@daedalus/atlas/BottomSheet';
import { Button } from '@daedalus/atlas/components/designSystem/Button';
import { Divider } from '@daedalus/atlas/components/designSystem/Divider';
import { PopUp } from '@daedalus/atlas/components/designSystem/PopUp';
import { Text } from '@daedalus/atlas/components/designSystem/Text';
import { useDeviceLayout } from '@daedalus/atlas/context/deviceLayout';
import { ContentWrapper } from '@daedalus/atlas/helpers/ContentWrapper';
import { Flex } from '@daedalus/atlas/helpers/Flex';
import { Grid } from '@daedalus/atlas/helpers/Grid';
import FormattedMessageWrapper from '@daedalus/core/src/localization/components/FormattedMessage';
import { getDatePart } from '@daedalus/core/src/utils/date';
interface Props {
  title: ReactNode;
  message?: ReactNode;
  isOpen: boolean;
  closeModal: () => void;
  transitionDate: string;
  checkInDate: string;
  checkOutDate: string;
}
interface InstructionRowProps extends Pick<Props, 'transitionDate'> {
  title: JSX.Element;
  message?: JSX.Element;
  isHighlighted?: boolean;
  dataId?: string;
}
interface DateBoxProps extends Pick<InstructionRowProps, 'isHighlighted'> {
  date: string;
  dataId?: string;
}
interface ContentProps extends Pick<Props, 'transitionDate' | 'checkInDate' | 'checkOutDate' | 'message'> {}
const _exp = /*#__PURE__*/() => Flex.Column;
const DateBoxWrapper = /*#__PURE__*/styled(_exp())({
  name: "DateBoxWrapper",
  class: "d1mh6qc8",
  propsAsIs: true
});
const Wrapper = /*#__PURE__*/styled('div')({
  name: "Wrapper",
  class: "w17zp53x",
  propsAsIs: false
});
const DateBox = ({
  date,
  isHighlighted,
  dataId
}: DateBoxProps) => {
  return <DateBoxWrapper className={cx(isHighlighted && 'isHighlighted')}>
      <Text variant="bodyXS" colorPath={isHighlighted ? 'content.highlight.c950' : 'content.neutral.c600'} dataId={`splitBooking.month.${dataId}`}>
        <FormattedDate value={date} month="short" />
      </Text>
      <Text variant="labelS" colorPath={isHighlighted ? 'content.highlight.c950' : 'content.neutral.c950'} dataId={`splitBooking.date.${dataId}`}>
        <FormattedDate value={date} day="2-digit" />
      </Text>
    </DateBoxWrapper>;
};
const InstructionRow = ({
  transitionDate,
  title,
  message,
  isHighlighted,
  dataId
}: InstructionRowProps) => {
  return <Grid container gap="s500" spacing="s000" direction="row" wrap="nowrap">
      <DateBox date={transitionDate} isHighlighted={isHighlighted} dataId={dataId} />

      <Grid container spacing="s000" justify="center" direction="column">
        <Text variant="titleM">{title}</Text>
        <Text variant="bodyS" colorPath="content.neutral.c600">
          {message}
        </Text>
      </Grid>
    </Grid>;
};
const Content = ({
  message,
  transitionDate,
  checkInDate,
  checkOutDate
}: ContentProps) => {
  return <Wrapper>
      {message && <ContentWrapper paddingBottom="s500">
          <Text variant="bodyM">{message}</Text>
        </ContentWrapper>}
      <ContentWrapper paddingY="s500">
        <InstructionRow title={<FormattedMessageWrapper id="checkIn" defaultMessage="Check-in" />} message={<FormattedMessageWrapper id="splitBooking.checkInMessage" defaultMessage="Remind the front desk that you have two bookings." />} transitionDate={checkInDate} dataId="checkIn" />
      </ContentWrapper>
      <Divider />
      <ContentWrapper paddingY="s500">
        <InstructionRow title={<FormattedMessageWrapper id="splitBooking.switchRoom" defaultMessage="Switch room" />} message={<FormattedMessageWrapper id="splitBooking.transitionDateMessage" defaultMessage="If required by the property, you’ll switch rooms on {transitionDate}" values={{
        transitionDate: <FormattedDate value={getDatePart(transitionDate)} day="2-digit" month="short" timeZone="UTC" />
      }} />} transitionDate={transitionDate} isHighlighted dataId="transitionTime" />
      </ContentWrapper>
      <Divider />
      <ContentWrapper paddingY="s500">
        <InstructionRow title={<FormattedMessageWrapper id="checkOut" defaultMessage="Check-out" />} transitionDate={checkOutDate} dataId="checkOut" />
      </ContentWrapper>
    </Wrapper>;
};
export const SplitBookingInstructionModal = ({
  isOpen,
  closeModal,
  title,
  message,
  transitionDate,
  checkInDate,
  checkOutDate
}: Props) => {
  const {
    isMobile
  } = useDeviceLayout();
  if (isMobile) {
    return <BottomSheet isOpen={isOpen} titleBarProps={{
      centerContent: <Text variant="titleM">{title}</Text>
    }} onClose={closeModal} footerContent={<ContentWrapper paddingBottom="s600" paddingX="s500">
            <Button variant="secondary" size="lg" onClick={closeModal} fullWidth>
              <Text variant="labelM">
                <FormattedMessageWrapper id="gotItButton" defaultMessage="Ok, got it" />
              </Text>
            </Button>
          </ContentWrapper>}>
        <ContentWrapper paddingTop="s300">
          <Content message={message} transitionDate={transitionDate} checkInDate={checkInDate} checkOutDate={checkOutDate} />
        </ContentWrapper>
      </BottomSheet>;
  }
  return <PopUp isOpen={isOpen} closeAction={closeModal} titleBarProps={{
    centerContent: <Text variant="titleL">{title}</Text>
  }} width={520} footer={<Button variant="secondary" size="lg" onClick={closeModal} fullWidth>
          <Text variant="labelM">
            <FormattedMessageWrapper id="gotItButton" defaultMessage="Ok, got it" />
          </Text>
        </Button>}>
      <Content message={message} transitionDate={transitionDate} checkInDate={checkInDate} checkOutDate={checkOutDate} />
    </PopUp>;
};

require("./index.wyw-in-js.css!=!../../../../../node_modules/@wyw-in-js/webpack-loader/lib/outputCssLoader.js?cacheProvider=!./index.tsx");